import React, { useState, useEffect, useRef } from"react";
import { useNavigate } from"react-router-dom";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import ReactMarkdown from"react-markdown";
import remarkGfm from"remark-gfm";
import { useSubscription } from "../Context/SubscriptionContext";
import FeatureGuard from "./access/FeatureGuard";
import API_BASE_URL from "../Config/config";

const InterviewPage = () => {
 return (
  <FeatureGuard feature="TSG_AI">
    <InterviewContent />
  </FeatureGuard>
 );
};

const InterviewContent = () => {
 const [input, setInput] = useState("");
 const [chat, setChat] = useState([]);
 const [loading, setLoading] = useState(false);
 const chatEndRef = useRef(null);
 const navigate = useNavigate();

 const handleAsk = async () => {
 if (!input.trim()) return;
 const currentInput = input;
 setChat((prev) => [...prev, { role: "user", content: currentInput }]);
 setInput("");
 setLoading(true);

 const user = JSON.parse(localStorage.getItem("user") ||"{}");
 const userId = user?.userId ||"UnknownUser";

 if (!userId || userId ==="UnknownUser") {
 alert("Please log in to use this feature.");
 navigate("/login");
 return;
 }

 try {
 const res = await fetch(`${API_BASE_URL}/tsg/interview_qa`, {
 method:"POST",
 headers: {"Content-Type":"application/json" },
 body: JSON.stringify({
 userId,
 prompt: currentInput,
 chatHistory: chat,
 language:"en",
 technology:"general",
 topic:"general",
 }),
 });

 const data = await res.json();
 const reply = data.reply ||"No reply.";
 setChat((prev) => [
 ...prev,
 { role:"assistant", content: reply }
 ]);
 } catch {
 setChat((prev) => [
 ...prev,
 { role:"assistant", content:"❌ Error reaching TSG." }
 ]);
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
    const container = chatEndRef.current?.parentElement;
    if (container) {
      const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 100;
      if (isAtBottom || chat.length <= 2) {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [chat]);

  return (
    <div className="p-4 md:p-8 min-h-screen bg-[#F8FAFC] text-[#111827] font-sans">
      <div className="max-w-4xl mx-auto flex flex-col h-[calc(100vh-120px)] premium-card p-6 md:p-8">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors group"
              title="Go Back"
            >
              <ChevronLeftIcon className="w-6 h-6 text-[#5B21B6] group-hover:-translate-x-1 transition-transform" />
            </button>
            <div className="p-3 bg-purple-50 rounded-xl border-2 border-[#5B21B6] shadow-sm">
              <svg className="w-8 h-8 text-[#5B21B6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <div>
              <h2 className="text-3xl font-black text-[#111827] uppercase tracking-tight">
                @tsg <span className="text-[#5B21B6]">→</span> Interview Q&A
              </h2>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mt-1">Professional interview preparation assistant</p>
            </div>
          </div>
        </div>

        {/* Chat Messages Area */}
        <div className="flex-1 overflow-y-auto mb-6 pr-2 custom-scrollbar space-y-6">
          {chat.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center space-y-4 opacity-40">
              <div className="w-20 h-20 rounded-full bg-purple-50 flex items-center justify-center border border-purple-100">
                <svg className="w-10 h-10 text-[#5B21B6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0012 18.75c-1.03 0-1.9.693-2.166 1.638m7.347-11.238l.707.707a5 5 0 010 7.072l-.707.707" />
                </svg>
              </div>
              <div>
                <p className="font-bold uppercase tracking-widest text-sm text-[#111827]">Ready for your interview?</p>
                <p className="text-xs font-bold text-gray-400 mt-1 uppercase tracking-tighter">Type a topic to get practice questions and answers.</p>
              </div>
            </div>
          ) : (
            chat.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} animate-in fade-in slide-in-from-bottom-2 duration-300`}
              >
                <div
                  className={`max-w-[85%] md:max-w-[75%] p-5 rounded-[24px] shadow-sm border-2 transition-all ${
                    msg.role === "user"
                      ? "bg-[#5B21B6] text-white rounded-tr-none border-[#5B21B6] shadow-purple-100"
                      : "bg-[#F9F7FF] text-[#111827] rounded-tl-none border-[#5B21B6] shadow-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-[10px] font-black uppercase tracking-wider ${msg.role === "user" ? "text-purple-200" : "text-gray-400"}`}>
                      {msg.role === "user" ? "You" : "TSG Interviewer"}
                    </span>
                  </div>
                  <div className={`prose max-w-none prose-p:leading-relaxed ${msg.role === 'user' ? 'prose-p:text-white prose-headings:text-white prose-strong:text-white prose-code:text-white' : 'prose-p:text-gray-600 prose-headings:text-[#5B21B6] prose-strong:text-[#5B21B6]'}`}>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({ node, ...props }) => <p {...props} className="mb-0" />,
                        code: ({ node, inline, ...props }) => (
                          <code
                            {...props}
                            className={`${
                              inline
                                ? (msg.role === 'user' ? "bg-white/20 text-white px-1.5 py-0.5 rounded text-sm font-bold" : "bg-purple-50 text-[#5B21B6] px-1.5 py-0.5 rounded text-sm font-bold")
                                : "block bg-gray-900 text-green-400 p-4 rounded-xl border border-gray-800 font-mono text-sm my-4 overflow-x-auto"
                            }`}
                          />
                        ),
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Sticky Input Area */}
        <div className="relative">
          {loading && (
            <div className="absolute -top-10 left-4 flex items-center gap-2 text-[#5B21B6] text-[10px] font-black uppercase tracking-widest">
              <span className="flex space-x-1">
                <span className="w-1.5 h-1.5 bg-[#5B21B6] rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                <span className="w-1.5 h-1.5 bg-[#5B21B6] rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                <span className="w-1.5 h-1.5 bg-[#5B21B6] rounded-full animate-bounce"></span>
              </span>
              TSG is preparing questions...
            </div>
          )}
          <div className="flex items-center gap-3 p-2 bg-[#F9F7FF] rounded-[24px] border-2 border-[#5B21B6] shadow-xl shadow-purple-100 focus-within:ring-4 focus-within:ring-purple-50 transition-all">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleAsk()}
              placeholder="Enter topic for interview prep..."
              className="flex-1 bg-transparent px-4 py-3 text-sm font-bold text-[#111827] placeholder-gray-400 focus:outline-none"
            />
            <button
              onClick={handleAsk}
              disabled={loading || !input.trim()}
              className="p-4 rounded-2xl bg-gradient-to-r from-[#5B21B6] to-[#7C3AED] text-white hover:scale-105 active:scale-95 transition-all disabled:opacity-30 disabled:hover:scale-100 shadow-lg shadow-purple-200"
            >
              <svg className={`w-6 h-6 transform ${loading ? 'animate-spin' : 'hover:translate-x-1 hover:-translate-y-1'} transition-transform`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
          <p className="mt-4 text-center text-[10px] font-black text-[#94A3B8] uppercase tracking-widest">
             AI-POWERED BY THE SKILLGEN ELITE MASTERY SYSTEM
          </p>
        </div>
      </div>
    </div>
  );
};

export default InterviewPage;



